import { useCallback, useEffect, useState } from "react";
import { DEFAULT_META_COLUMNS } from "../pages/Console/metaColumns";

const STORAGE_KEY = "v1.console.metaColumns";

/**
 * Whether the Created / Updated columns show. Same shape and same reasoning as
 * useScreeningColumns: a display preference, so localStorage rather than a migration and an
 * endpoint for the privilege of remembering two checkboxes.
 *
 * Its own storage key, not a shared one: these columns appear on every tab and screening_data
 * columns only on Screening, so one key would make a Screening-only selection leak into the
 * default grid the moment the two arrays were concatenated.
 */
const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_META_COLUMNS;
    const parsed = JSON.parse(raw);
    // An empty array is a real choice (both toggled off) — only a non-array is corrupt.
    if (!Array.isArray(parsed)) return DEFAULT_META_COLUMNS;
    return parsed.filter((key) => typeof key === "string");
  } catch {
    return DEFAULT_META_COLUMNS;
  }
};

const useMetaColumns = () => {
  const [columns, setColumns] = useState(read);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
    } catch {
      // Private mode or a full quota. The choice just doesn't survive the reload — not worth
      // failing a render over.
    }
  }, [columns]);

  const toggle = useCallback((key) => {
    setColumns((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  }, []);

  return { columns, toggle };
};

export default useMetaColumns;
